<template>
  <div class="ibox float-e-margins mass-panel mass-table-panel">
    <div class="ibox-title">
      <h5>{{ labels.massUpdate }}</h5>
      <div class="ibox-tools">
        <span :class="{'mass-update-polling-pulse': pollTimer !== null}" class="mass-live-badge">
          <i v-if="pollTimer !== null" class="fa fa-circle fa-xs m-r-xs"
             style="font-size: 8px; vertical-align: middle;"></i>
          {{ labels.livePolling }}
        </span>
      </div>
    </div>
    <div class="ibox-content">
      <div v-if="loading" class="text-center m-b-md">
        <div class="sk-spinner sk-spinner-wave">
          <div class="sk-rect1"></div>
          <div class="sk-rect2"></div>
          <div class="sk-rect3"></div>
          <div class="sk-rect4"></div>
          <div class="sk-rect5"></div>
        </div>
        <div class="text-muted small m-t-sm">{{ labels.loading }}</div>
      </div>

      <div class="row mass-search-row">
        <div class="col-md-12">
          <div class="input-group mass-search-control">
            <input :placeholder="labels.searchPlaceholder" :value="searchQuery"
                   class="form-control input-sm"
                   type="text"
                   @input="$emit('update-search', $event.target.value)">
            <span class="input-group-btn">
              <button class="btn btn-sm btn-primary" type="button">
                <i class="fa fa-search"></i>
              </button>
            </span>
          </div>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <div class="table-responsive">
        <table class="table table-hover table-striped mass-config-table">
          <thead>
          <tr>
            <th style="width: 40px;">
              <input :checked="allSelected" type="checkbox" @change="$emit('toggle-select-all', $event)">
            </th>
            <th style="width: 60px;">{{ labels.id }}</th>
            <th>{{ labels.internalName }}</th>
            <th>{{ labels.appName }}</th>
            <th>{{ labels.client }}</th>
            <th style="width: 130px;">{{ labels.updatedAt }}</th>
            <th>{{ labels.status }}</th>
            <th class="text-right">{{ labels.actions }}</th>
          </tr>
          </thead>
          <tbody>
          <template v-for="config in paginatedConfigurations">
            <tr :key="config.id">
              <td>
                <input :checked="isSelected(config.id)" type="checkbox"
                       @change="toggleSelected(config.id, $event)">
              </td>
              <td class="small text-muted font-bold">
                #{{ config.id }}
              </td>
              <td>
                <div :title="config.internal_name"
                     class="font-bold"
                     style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 250px;">
                  {{ config.internal_name || '-' }}
                </div>
              </td>
              <td>
                <div class="font-bold">{{ config.app_name }}</div>
                <div class="text-muted small">{{ config.bundle_id }}</div>
              </td>
              <td>
                <div>{{ config.client_name || '-' }}</div>
                <div class="text-muted small">{{ config.target_repository || '-' }}</div>
              </td>
              <td>
                <div class="small text-muted" style="white-space: nowrap;">
                  {{ config.updated_at ? formatDate(config.updated_at) : '-' }}
                </div>
              </td>
              <td>
                <span v-if="currentBuild(config)" :class="statusBadgeClass(currentBuild(config).state)"
                      v-html="currentBuild(config).state_text"></span>
                <span v-else class="label label-default">{{ labels.noBuild }}</span>
              </td>
              <td class="text-right">
                <button class="btn btn-white btn-xs" @click="$emit('toggle-log', config)">
                  <i :class="showLog(config) ? 'fa-eye-slash' : 'fa-eye'" class="fa"></i>
                  {{ showLog(config) ? labels.hideLog : labels.showLog }}
                </button>
              </td>
            </tr>
            <tr v-if="showLog(config)" :key="`log-${config.id}`" class="bg-muted">
              <td colspan="8">
                <div class="p-xs">
                  <pre class="small"
                       style="white-space: pre-wrap; margin: 0; background: #2f4050; color: #fff; padding: 10px; border-radius: 4px;">{{
                      buildLog(config)
                    }}</pre>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="!loading && filteredConfigurations.length === 0">
            <td class="text-center text-muted p-md" colspan="8">
              {{ labels.noResults }}
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-if="totalPages > 1" class="row m-t-md">
        <div class="col-sm-5">
          <div class="text-muted small p-t-xs">
            {{ labels.showing }} {{ (currentPage - 1) * perPage + 1 }} -
            {{ Math.min(currentPage * perPage, filteredConfigurations.length) }} {{ labels.of }}
            {{ filteredConfigurations.length }} {{ labels.entries }}
          </div>
        </div>
        <div class="col-sm-7 text-right">
          <div class="btn-group">
            <button :disabled="currentPage === 1" class="btn btn-white btn-sm" @click="setPage(1)">
              <i class="fa fa-angle-double-left"></i>
            </button>
            <button :disabled="currentPage === 1" class="btn btn-white btn-sm" @click="setPage(currentPage - 1)">
              <i class="fa fa-angle-left"></i>
            </button>
            <button v-for="page in pagesToShow" :key="page"
                    :class="currentPage === page ? 'btn-primary' : 'btn-white'"
                    class="btn btn-sm"
                    @click="setPage(page)">
              {{ page }}
            </button>
            <button :disabled="currentPage === totalPages" class="btn btn-white btn-sm"
                    @click="setPage(currentPage + 1)">
              <i class="fa fa-angle-right"></i>
            </button>
            <button :disabled="currentPage === totalPages" class="btn btn-white btn-sm"
                    @click="setPage(totalPages)">
              <i class="fa fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {statusBadgeClass} from './buildStates';

export default {
  props: {
    labels: {
      type: Object,
      required: true,
    },
    pollTimer: {
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    errorMessage: {
      type: String,
      required: true,
    },
    searchQuery: {
      type: String,
      required: true,
    },
    allSelected: {
      type: Boolean,
      required: true,
    },
    selectedIds: {
      type: Array,
      required: true,
    },
    paginatedConfigurations: {
      type: Array,
      required: true,
    },
    filteredConfigurations: {
      type: Array,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    perPage: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    pagesToShow: {
      type: Array,
      required: true,
    },
    logOpen: {
      type: Object,
      required: true,
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },
    currentBuild(config) {
      return config.current_build || null;
    },
    statusBadgeClass(state) {
      return statusBadgeClass(state);
    },
    buildLog(config) {
      const build = this.currentBuild(config);
      if (!build || !build.log) {
        return this.labels.noLog;
      }
      return build.log;
    },
    showLog(config) {
      return !!this.logOpen[config.id];
    },
    setPage(page) {
      this.$emit('update-current-page', page);
    },
    isSelected(configId) {
      return this.selectedIds.some(selectedId => selectedId == configId);
    },
    toggleSelected(configId, event) {
      if (event.target.checked) {
        if (!this.isSelected(configId)) {
          this.$emit('update-selected-ids', this.selectedIds.concat([configId]));
        }
        return;
      }

      this.$emit('update-selected-ids', this.selectedIds.filter(selectedId => selectedId != configId));
    },
  },
};
</script>
